import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight, FileText } from "lucide-react";
import { Layout } from "@/components/Layout";
import { Seo } from "@/components/Seo";
import { POLICY_SECTIONS } from "@/data/policies";

export default function Policies() {
  const { hash } = useLocation();
  const [active, setActive] = useState(POLICY_SECTIONS[0].slug);

  useEffect(() => {
    if (!hash) return;
    const slug = hash.replace("#", "");
    const el = document.getElementById(slug);
    if (el) {
      setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 60);
      setActive(slug);
    }
  }, [hash]);

  useEffect(() => {
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setActive(e.target.id);
        });
      },
      { rootMargin: "-40% 0px -55% 0px", threshold: 0 }
    );
    POLICY_SECTIONS.forEach((s) => {
      const el = document.getElementById(s.slug);
      if (el) io.observe(el);
    });
    return () => io.disconnect();
  }, []);

  return (
    <Layout>
      <Seo
        title="Policies, Complaints & Lost Property"
        description="Our terms, privacy, cookies, complaints, lost property, cancellation, safeguarding and accessibility policies. Clocktower Cars Gatwick."
        path="/policies"
      />

      {/* Hero */}
      <section className="bg-ink py-20 md:py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <p className="text-xs font-bold uppercase tracking-[0.25em] mb-4 text-brand-light">Company</p>
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-black text-white tracking-tight leading-[1.05] max-w-3xl">
            Policies, complaints <span className="text-white/50">& lost property</span>
          </h1>
          <p className="mt-5 text-white/60 max-w-2xl leading-relaxed">
            The full set of Clocktower Cars policies. If you have a question we have not answered here, please contact us on 01293 515151 or bookings@clocktowercarsuk.com.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to="/complaints"
              data-testid="hero-complaints-cta"
              className="inline-flex items-center gap-2 bg-brand hover:bg-brand-light text-white px-6 py-3 rounded-full font-semibold transition-colors"
            >
              Submit a complaint <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to="/lost-property"
              data-testid="hero-lost-cta"
              className="inline-flex items-center gap-2 border border-white/30 hover:border-white text-white px-6 py-3 rounded-full font-semibold transition-colors"
            >
              Report a lost item <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="bg-white py-14 md:py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid gap-12 lg:grid-cols-12">
          {/* Side nav */}
          <aside className="lg:col-span-3">
            <div className="lg:sticky lg:top-28">
              <p className="text-[11px] uppercase tracking-[0.25em] text-ink/50 font-bold mb-4">On this page</p>
              <nav className="flex flex-col gap-1">
                {POLICY_SECTIONS.map((s) => (
                  <a
                    key={s.slug}
                    href={`#${s.slug}`}
                    data-testid={`nav-${s.slug}`}
                    className={`text-sm py-2 px-3 rounded-lg border-l-2 transition-colors ${
                      active === s.slug
                        ? "text-ink font-semibold border-brand bg-surface"
                        : "text-ink/60 border-transparent hover:text-ink hover:bg-surface"
                    }`}
                  >
                    {s.title}
                  </a>
                ))}
              </nav>
            </div>
          </aside>

          {/* Sections */}
          <div className="lg:col-span-9 space-y-16">
            {POLICY_SECTIONS.map((s) => (
              <article
                key={s.slug}
                id={s.slug}
                data-testid={`policy-${s.slug}`}
                className="scroll-mt-28"
              >
                <div className="flex items-center gap-2 text-brand text-[11px] font-bold uppercase tracking-[0.25em] mb-3">
                  <FileText className="h-3.5 w-3.5" /> Updated {s.updated}
                </div>
                <h2 className="font-heading text-3xl md:text-4xl font-bold text-ink tracking-tight leading-tight mb-5">
                  {s.title}
                </h2>
                <p className="text-ink/70 leading-relaxed text-[17px] mb-8">{s.intro}</p>

                <div className="space-y-6">
                  {s.blocks.map((b) => (
                    <div key={b.h}>
                      <h3 className="font-heading text-lg font-bold text-ink mb-2">{b.h}</h3>
                      <p className="text-ink/70 leading-relaxed">{b.p}</p>
                    </div>
                  ))}
                </div>

                {s.ctaHref && (
                  <Link
                    to={s.ctaHref}
                    data-testid={`cta-${s.slug}`}
                    className="mt-8 inline-flex items-center gap-2 bg-ink hover:bg-brand text-white px-6 py-3 rounded-full font-semibold transition-colors"
                  >
                    {s.ctaLabel} <ArrowRight className="h-4 w-4" />
                  </Link>
                )}
              </article>
            ))}
          </div>
        </div>
      </section>
    </Layout>
  );
}
